import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileStack, MoreVertical, Plus, Star, Trash2, X } from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { SettingsCard } from '../components/SettingsCard';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';
import {
  clearDefaultDocumentLayoutTemplate,
  createDocumentLayoutTemplate,
  deleteDocumentLayoutTemplate,
  fetchDocumentLayoutTemplates,
  setDefaultDocumentLayoutTemplate,
} from '../utils/documentLayoutApi';
import type { DocumentLayoutTemplateRow } from '../utils/documentLayoutTypes';
import { invoiceTemplateSettingsPath } from '../../../utils/settingsRoutes';

type ConfirmAction =
  | { type: 'setDefault'; tpl: DocumentLayoutTemplateRow }
  | { type: 'delete'; tpl: DocumentLayoutTemplateRow };

function withInstance(path: string): string {
  const match = window.location.pathname.match(/^\/i\/[^/]+/);
  const prefix = match ? match[0] : '/i/default';
  return `${prefix}${path.startsWith('/') ? path : `/${path}`}`;
}

export function InvoiceTemplateSettingsSection() {
  const navigate = useNavigate();
  const [templates, setTemplates] = React.useState<DocumentLayoutTemplateRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<ConfirmAction | null>(null);
  const menuTriggerRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const menuPanelRef = React.useRef<HTMLDivElement | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchDocumentLayoutTemplates();
      setTemplates(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const openMenuTpl = openMenuId
    ? templates.find((t) => t.id === openMenuId) ?? null
    : null;

  React.useEffect(() => {
    if (!openMenuId) {
      setMenuPos(null);
      return;
    }
    const trigger = menuTriggerRefs.current.get(openMenuId);
    if (!trigger) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const rect = trigger.getBoundingClientRect();
      const menuWidth = 220;
      const left = Math.min(
        Math.max(8, rect.right - menuWidth),
        window.innerWidth - menuWidth - 8,
      );
      setMenuPos({ top: rect.bottom + 4, left });
    };
    place();
    const onScroll = () => place();
    const onResize = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [openMenuId]);

  React.useEffect(() => {
    if (!openMenuId) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const trigger = menuTriggerRefs.current.get(openMenuId);
      if (menuPanelRef.current?.contains(target) || trigger?.contains(target)) return;
      setOpenMenuId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const onCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      // Never auto-live: user must set „Als Standard“ explicitly.
      const tpl = await createDocumentLayoutTemplate({
        name: `Vorlage ${templates.length + 1}`,
        is_default: false,
      });
      navigate(withInstance(invoiceTemplateSettingsPath(tpl.id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erstellen fehlgeschlagen.');
    } finally {
      setCreating(false);
    }
  };

  const openTemplate = (id: string) => {
    navigate(withInstance(invoiceTemplateSettingsPath(id)));
  };

  const requestSetDefault = (tpl: DocumentLayoutTemplateRow) => {
    setOpenMenuId(null);
    setConfirmAction({ type: 'setDefault', tpl });
  };

  const requestDelete = (tpl: DocumentLayoutTemplateRow) => {
    setOpenMenuId(null);
    setConfirmAction({ type: 'delete', tpl });
  };

  const onClearDefault = async (id: string) => {
    setOpenMenuId(null);
    setBusyId(id);
    setError(null);
    try {
      await clearDefaultDocumentLayoutTemplate(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Standard aufheben fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const onConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, tpl } = confirmAction;
    setBusyId(tpl.id);
    setError(null);
    try {
      if (type === 'setDefault') {
        await setDefaultDocumentLayoutTemplate(tpl.id);
      } else {
        await deleteDocumentLayoutTemplate(tpl.id);
      }
      setConfirmAction(null);
      await reload();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : type === 'setDefault'
            ? 'Standard setzen fehlgeschlagen.'
            : 'Löschen fehlgeschlagen.',
      );
      setConfirmAction(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title="Rechnungsvorlage"
        description="Gestalten Sie Kopf- und Fußbereich Ihrer Dokumente. Positionstabelle, Summen und USt-Hinweise bleiben unverändert. Neue Vorlagen sind Entwürfe und greifen erst nach „Als Standard setzen“. Entwürfe dürfen Fehler haben und gespeichert werden; als Standard (live) lässt sich nur eine gültige Vorlage setzen. Ohne Standard-Vorlage gilt weiterhin das native Dokument-Layout."
        actions={
          <button
            type="button"
            className={SETTINGS_BTN_PRIMARY}
            disabled={creating}
            onClick={() => void onCreate()}
          >
            {creating ? <LoadingSpinner /> : <Plus className="w-4 h-4" />}
            Neue Vorlage
          </button>
        }
      >
        {error ? (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div>
          <p className={SETTINGS_FIELD_LABEL}>Meine Vorlagen</p>
          {loading ? (
            <InlineLoadingState label="Laden…" className="justify-start py-8" />
          ) : templates.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-app-border bg-app-surface-muted px-4 py-8 text-center">
              <FileStack className="mx-auto h-8 w-8 text-slate-300" strokeWidth={1.5} />
              <p className="mt-2 text-sm text-app-text-muted">
                Noch keine Vorlage. Es gilt das native Dokument-Layout, bis Sie eine Vorlage
                speichern und als Standard setzen.
              </p>
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-app-border rounded-md border border-app-border bg-app-surface">
              {templates.map((tpl) => (
                <li key={tpl.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex cursor-pointer flex-col gap-3 px-4 py-3 transition-colors hover:bg-app-hover sm:flex-row sm:items-center sm:justify-between focus:outline-none focus-visible:bg-app-surface-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pipedrive-blue/40"
                    onClick={() => openTemplate(tpl.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openTemplate(tpl.id);
                      }
                    }}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-app-text">
                          {tpl.name}
                        </span>
                        {tpl.is_default ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                            <Star className="h-3 w-3" />
                            Standard
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-xs text-app-text-subtle">
                        Version {tpl.version}
                        {tpl.updated_at
                          ? ` · aktualisiert ${new Date(tpl.updated_at).toLocaleString('de-DE')}`
                          : ''}
                      </p>
                    </div>
                    <div
                      className="relative shrink-0 self-end sm:self-center"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) menuTriggerRefs.current.set(tpl.id, el);
                          else menuTriggerRefs.current.delete(tpl.id);
                        }}
                        disabled={busyId === tpl.id}
                        onClick={() =>
                          setOpenMenuId((current) => (current === tpl.id ? null : tpl.id))
                        }
                        className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-app-hover hover:text-app-text-muted disabled:opacity-50"
                        aria-label="Aktionen"
                        aria-expanded={openMenuId === tpl.id}
                        aria-haspopup="menu"
                      >
                        {busyId === tpl.id ? (
                          <LoadingSpinner />
                        ) : (
                          <MoreVertical className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SettingsCard>

      {openMenuTpl && menuPos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuPanelRef}
              role="menu"
              style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 300 }}
              className="min-w-[220px] rounded-xl border border-app-border bg-app-surface p-1 shadow-lg"
            >
              {openMenuTpl.is_default ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void onClearDefault(openMenuTpl.id)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
                >
                  <Star className="h-4 w-4 text-app-text-subtle" />
                  Standard aufheben
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => requestSetDefault(openMenuTpl)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
                >
                  <Star className="h-4 w-4 text-app-text-subtle" />
                  Als Standard setzen
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => requestDelete(openMenuTpl)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>,
            document.body,
          )
        : null}

      {confirmAction?.type === 'setDefault' ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => {
            if (!busyId) setConfirmAction(null);
          }}
        >
          <div
            className="relative w-full max-w-md overflow-hidden rounded-xl border border-amber-200 bg-app-surface shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-template-set-default-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                if (!busyId) setConfirmAction(null);
              }}
              disabled={Boolean(busyId)}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-900 hover:bg-amber-100 hover:text-amber-950 disabled:opacity-40"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="border-b border-amber-100 bg-amber-50 px-5 py-4 pr-12">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0 rounded-full bg-amber-100 p-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3
                    id="invoice-template-set-default-title"
                    className="text-base font-semibold text-amber-950"
                  >
                    Achtung: Als Standard-Vorlage setzen?
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-amber-950">
                    <span className="font-medium text-amber-950">„{confirmAction.tpl.name}“</span>{' '}
                    wird ab sofort für alle neuen Rechnungen, Angebote, Kostenvoranschläge und
                    weiteren Belege verwendet.
                  </p>
                  <p className="mt-3 text-sm font-medium leading-relaxed text-amber-950">
                    Bereits finalisierte Dokumente bleiben unverändert.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 bg-app-surface px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={Boolean(busyId)}
                className={SETTINGS_BTN_OUTLINE}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void onConfirmAction()}
                disabled={Boolean(busyId)}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-amber-600 px-3.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-40"
              >
                {busyId ? <LoadingSpinner /> : null}
                Als Standard setzen
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AppConfirmDialog
        open={confirmAction?.type === 'delete'}
        onClose={() => !busyId && setConfirmAction(null)}
        title="Vorlage löschen?"
        description={
          confirmAction?.type === 'delete' ? (
            confirmAction.tpl.is_default ? (
              <>
                <span className="font-medium text-app-text">„{confirmAction.tpl.name}“</span>{' '}
                wirklich löschen? Ohne Standard-Vorlage gilt wieder das native Dokument-Layout.
              </>
            ) : (
              <>
                <span className="font-medium text-app-text">„{confirmAction.tpl.name}“</span>{' '}
                wirklich löschen?
              </>
            )
          ) : null
        }
        confirmLabel="Löschen"
        destructive
        isLoading={Boolean(busyId && confirmAction?.type === 'delete')}
        onConfirm={() => void onConfirmAction()}
      />
    </div>
  );
}
